import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Search, Download, ChevronLeft, ChevronRight, Check, ArrowUpDown } from 'lucide-react';
import { api, JournalEntry, PaginatedResponse } from '../../lib/api';
import { exportToCsv, CsvColumn } from '../../lib/csv';

const journalCsvColumns: CsvColumn[] = [
  { key: 'journalNumber', label: '仕訳番号' },
  { key: 'journalDate', label: '日付' },
  { key: 'journalType', label: '種類' },
  { key: 'description', label: '摘要' },
  { key: 'status', label: 'ステータス' },
  { key: 'totalDebit', label: '借方合計' },
  { key: 'totalCredit', label: '貸方合計' },
];

export function JournalListPage() {
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(today.getDate() - 30);

  const [startDate, setStartDate] = useState(thirtyDaysAgo.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [sortField, setSortField] = useState<'journalDate' | 'journalNumber'>('journalDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['journals', { startDate, endDate, status, page }],
    queryFn: () =>
      api.get<PaginatedResponse<JournalEntry>>('/api/journals', {
        startDate,
        endDate,
        status: status || '',
        page: String(page),
        limit: '20',
      }),
  });

  const handleApprove = async (id: string) => {
    try {
      await api.post(`/api/journals/${id}/approve`);
      refetch();
    } catch (err) {
      console.error(err);
    }
  };

  const handleExportCsv = () => {
    if (data?.data) {
      exportToCsv(data.data, journalCsvColumns, '仕訳一覧');
    }
  };

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  // クライアント側でソート
  const sortedData = [...(data?.data || [])].sort((a, b) => {
    const aVal = a[sortField] || '';
    const bVal = b[sortField] || '';
    const cmp = aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
          仕訳一覧
        </h1>
        <Link
          to="/journals/create"
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium transition-colors"
          style={{ backgroundColor: 'var(--accent-color)' }}
        >
          <Plus size={18} />
          新規仕訳
        </Link>
      </div>

      {/* Search filters */}
      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 rounded-lg border text-sm"
              style={{
                backgroundColor: 'var(--input-bg)',
                borderColor: 'var(--input-border)',
                color: 'var(--text-primary)',
              }}
            />
            <span style={{ color: 'var(--text-secondary)' }}>〜</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 rounded-lg border text-sm"
              style={{
                backgroundColor: 'var(--input-bg)',
                borderColor: 'var(--input-border)',
                color: 'var(--text-primary)',
              }}
            />
          </div>

          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="px-3 py-2 rounded-lg border text-sm"
            style={{
              backgroundColor: 'var(--input-bg)',
              borderColor: 'var(--input-border)',
              color: 'var(--text-primary)',
            }}
          >
            <option value="">全ステータス</option>
            <option value="draft">下書き</option>
            <option value="posted">転記済</option>
            <option value="void">取消</option>
          </select>

          <div className="relative flex-1 max-w-xs">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
              style={{ color: 'var(--text-muted)' }}
            />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="検索..."
              className="w-full pl-10 pr-4 py-2 rounded-lg border text-sm"
              style={{
                backgroundColor: 'var(--input-bg)',
                borderColor: 'var(--input-border)',
                color: 'var(--text-primary)',
              }}
            />
          </div>

          <button
            onClick={() => refetch()}
            className="px-4 py-2 rounded-lg text-white text-sm font-medium"
            style={{ backgroundColor: 'var(--accent-color)' }}
          >
            反映
          </button>

          <button
            onClick={handleExportCsv}
            disabled={!data?.data?.length}
            className="px-4 py-2 rounded-lg border text-sm disabled:opacity-50"
            style={{
              borderColor: 'var(--border-color)',
              color: 'var(--text-primary)',
            }}
          >
            <Download size={16} className="inline mr-1" />
            CSV出力
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table>
              <thead>
                <tr>
                  <th 
                    className="cursor-pointer select-none"
                    onClick={() => toggleSort('journalNumber')}
                    style={{ color: 'var(--text-primary)' }}
                  >
                    <span className="flex items-center gap-1">
                      仕訳番号
                      <ArrowUpDown size={14} className={sortField === 'journalNumber' ? 'opacity-100' : 'opacity-30'} />
                    </span>
                  </th>
                  <th 
                    className="cursor-pointer select-none"
                    onClick={() => toggleSort('journalDate')}
                    style={{ color: 'var(--text-primary)' }}
                  >
                    <span className="flex items-center gap-1">
                      日付
                      <ArrowUpDown size={14} className={sortField === 'journalDate' ? 'opacity-100' : 'opacity-30'} />
                    </span>
                  </th>
                  <th style={{ color: 'var(--text-primary)' }}>種類</th>
                  <th style={{ color: 'var(--text-primary)' }}>摘要</th>
                  <th style={{ color: 'var(--text-primary)' }}>ステータス</th>
                  <th style={{ color: 'var(--text-primary)' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
                      データがありません
                    </td>
                  </tr>
                ) : (
                  sortedData.map((journal) => (
                    <tr key={journal.id}>
                      <td className="font-mono" style={{ color: 'var(--text-primary)' }}>
                        {journal.journalNumber}
                      </td>
                      <td style={{ color: 'var(--text-primary)' }}>{journal.journalDate}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>
                        {journal.journalType === 'normal' ? '通常' : journal.journalType === 'adjustment' ? '決算整理' : '締切'}
                      </td>
                      <td style={{ color: 'var(--text-secondary)' }} className="max-w-xs truncate">
                        {journal.description || '-'}
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            journal.status === 'posted'
                              ? 'badge-success'
                              : journal.status === 'draft'
                              ? 'badge-warning'
                              : 'badge-error'
                          }`}
                        >
                          {journal.status === 'posted' ? '転記済' : journal.status === 'draft' ? '下書き' : '取消'}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          {journal.status === 'draft' && (
                            <button
                              onClick={() => handleApprove(journal.id)}
                              className="p-1.5 rounded hover:bg-green-50 dark:hover:bg-green-900/20 text-green-600"
                              title="転記"
                            >
                              <Check size={16} />
                            </button>
                          )}
                          <Link
                            to={`/journals/${journal.id}`}
                            className="text-sm hover:underline"
                            style={{ color: 'var(--accent-color)' }}
                          >
                            詳細
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div
          className="flex items-center justify-between px-4 py-3 border-t"
          style={{ borderColor: 'var(--border-color)' }}
        >
          <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            {data?.data.length || 0} 件表示
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 rounded-lg border disabled:opacity-50"
              style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm px-2" style={{ color: 'var(--text-primary)' }}>
              {page}
            </span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={(data?.data.length || 0) < 20}
              className="p-2 rounded-lg border disabled:opacity-50"
              style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

